import React, { type ComponentProps } from 'react'

import { SemanticColorToken } from '../../themes'
import type { Icon12 } from '../FoundationIcon/icons'
import {
  getNeutralColorVars,
  getSemanticColorVars,
  useSemanticColorComponents,
} from '../ThemeProvider'
import { StepIcon } from './StepIcon'
import type { StepLabel } from './StepLabel'
import type { TimelineOrientation, TimelineStatus } from './types'
import { SMALL_DEVICE_BREAKPOINT } from './utils'

type Props = {
  isIconOnly: boolean
  isResponsive: boolean
  gridArea: string
  status: TimelineStatus
  orientation: TimelineOrientation
  iconOverride?: Icon12
}

export function StepLine({
  gridArea,
  iconOverride,
  isIconOnly,
  isResponsive,
  orientation,
  status,
}: Props) {
  const lineColor = useLineColor(status, orientation)
  const verticalLineColor = useLineColor(status, 'vertical')

  return (
    <div
      css={[
        {
          alignItems: 'center',
          display: 'flex',
          gridArea,
        },
        getLineStyles(orientation),
        isResponsive && {
          [`@media screen and (max-width: ${SMALL_DEVICE_BREAKPOINT}px)`]:
            getLineStyles('vertical'),
        },
      ]}
    >
      <StepIcon iconOverride={iconOverride} status={status} />
      {!isIconOnly && (
        <div
          css={[
            {
              background: lineColor,
              flexGrow: 1,
            },
            getIconStyles(orientation),
            isResponsive && {
              [`@media screen and (max-width: ${SMALL_DEVICE_BREAKPOINT}px)`]: [
                getIconStyles('vertical'),
                { background: verticalLineColor },
              ],
            },
          ]}
        />
      )}
    </div>
  )
}

function useLineColor(
  status: ComponentProps<typeof StepLabel>['status'],
  orientation: TimelineOrientation
) {
  const { b, g, r } = useSemanticColorComponents(SemanticColorToken.Positive)
  const neutralColorVars = getNeutralColorVars()
  const semanticColorVars = getSemanticColorVars()

  switch (status) {
    case 'ongoing': {
      return `linear-gradient(to ${
        orientation === 'vertical' ? 'bottom' : 'right'
      }, rgba(${r},${g},${b},1) 0%, rgba(${r},${g},${b},.75) 60%, rgba(${r},${g},${b},0) 100%)`
    }
    case 'complete': {
      return semanticColorVars.positive.base
    }
    case 'incomplete':
    case 'paused':
    case 'warning':
    case 'error':
    default: {
      return neutralColorVars.bg3
    }
  }
}

function getLineStyles(
  orientation: TimelineOrientation
): { flexDirection: 'row' | 'column'; transform: string } | undefined {
  switch (orientation) {
    case 'horizontal-center-align': {
      return {
        flexDirection: 'row',
        transform: 'translate(-12px)',
      }
    }
    case 'vertical': {
      return {
        flexDirection: 'column',
        transform: 'none',
      }
    }
    default: {
      return undefined
    }
  }
}

function getIconStyles(
  orientation: TimelineOrientation
): { height: string; width: string } | undefined {
  switch (orientation) {
    case 'horizontal-left-align': {
      return {
        height: '2px',
        width: 'auto',
      }
    }
    case 'horizontal-center-align': {
      return {
        height: '2px',
        width: 'auto',
      }
    }
    case 'vertical': {
      return {
        height: 'auto',
        width: '2px',
      }
    }
    default: {
      return undefined
    }
  }
}
